/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import MembershipSignInModal from "./MembershipSignInModal";
import MembershipRequiredModal from "./MembershipRequiredModal";
import MembershipChooseModal from "./MembershipChooseModal";
import { useServiceMembershipAccess, ACCESS_STATUS } from "../../hooks/useServiceMembershipAccess";
import {
  clearPendingAccessService,
  setPendingAccessService,
  setSelectedServices,
  setSelectedProvider,
  setMembershipAccessGrant,
  clearMembershipAccessGrants,
} from "../../store/slices/bookingSlice";
import useServiceSelection from "../../hooks/useServiceSelection";
import { useBooking } from "../../hooks/useBooking";
import usePluginUtils from "../../hooks/usePluginUtils";
import { useAuth } from "../../hooks/useAuth";
import { useTranslate } from "../../hooks/useTranslate";
import { useToast } from "../ui/Toast";
import { ROUTES, WIDGET_CALLBACK_URL } from "../../constants/constants";
import {
  createMembershipGrant,
  getMembershipCoverageWindow,
  isPurchaseDateCompatible,
} from "../../utils/membershipGrant";
import { getClassStartRoute, isClassService } from "../../utils/classes";

// Do not show the modal on these routes. /login is in the list because we
// send the user to /login to sign in, and we keep pendingAccessService set
// so we know to continue the booking after login.
const isSuppressedRoute = (pathname) =>
  pathname.startsWith(ROUTES.BOOKING_STATUS) ||
  pathname.startsWith(ROUTES.CART_ITEM_STATUS) ||
  pathname === ROUTES.LOGIN;
const isInPaymentCallback = () =>
  typeof window !== "undefined" && window.location.hash.includes(WIDGET_CALLBACK_URL);

/**
 * Mounted once in Layout. Blocks a members-only service from being booked
 * until the user signs in and has a valid membership. Works from any entry
 * route (deep link, scheduler, Home, Services).
 *
 * After login: we kept pendingAccessService set, so the access check runs
 * again with the new token. If the user now has access, we re-add the
 * service. If they still need a membership, we show the required modal.
 */
const MembershipServiceAccessModals = () => {
  const dispatch = useDispatch();
  const { isPluginEnabled } = usePluginUtils();
  const membershipPluginEnabled = isPluginEnabled("membership");
  const location = useLocation();
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const { _t } = useTranslate();
  const { toast } = useToast();
  const {
    pendingAccessService: pendingService,
    selectedServices,
    membershipAccessGrants,
  } = useBooking();
  const { status, requiredMemberships, clientMembership, usableMemberships, exhaustedMemberships } =
    useServiceMembershipAccess(pendingService);
  const { applyServiceSelection } = useServiceSelection(null);
  const resumedRef = useRef(null);
  const lastWatchedIdRef = useRef(null);
  const justResumedRef = useRef(null);
  const exhaustedToastRef = useRef(null);
  const prevPathnameRef = useRef(location.pathname);

  // Auth lost while a service is selected: drop stale grants and re-arm the
  // watch effect (null the ref) so it re-checks access. Must run before it.
  useEffect(() => {
    if (!membershipPluginEnabled) return;
    if (isAuthenticated) return;
    if (Object.keys(membershipAccessGrants).length === 0) return;

    dispatch(clearMembershipAccessGrants());
    lastWatchedIdRef.current = null;
  }, [membershipPluginEnabled, isAuthenticated, membershipAccessGrants, dispatch]);

  useEffect(() => {
    if (!membershipPluginEnabled) return;

    const candidate = selectedServices?.[0];

    if (!candidate) {
      lastWatchedIdRef.current = null;
      return;
    }

    if (justResumedRef.current === candidate.id) {
      // The GRANTED effect just re-added this service. Skip the check so we
      // do not block the user a second time.
      justResumedRef.current = null;
      lastWatchedIdRef.current = candidate.id;
      return;
    }

    if (lastWatchedIdRef.current === candidate.id) return;
    if (!Array.isArray(candidate.memberships) || candidate.memberships.length === 0) return;
    if (isSuppressedRoute(location.pathname) || isInPaymentCallback()) return;

    lastWatchedIdRef.current = candidate.id;

    dispatch(setPendingAccessService(candidate));
    dispatch(setSelectedServices(selectedServices.filter((s) => s.id !== candidate.id)));
    if (isClassService(candidate)) dispatch(setSelectedProvider(null));
  }, [membershipPluginEnabled, selectedServices, location.pathname, isAuthenticated, dispatch]);

  // If the user leaves /login without signing in (clicks the header, hits
  // back, etc.), clear pendingAccessService. Otherwise the sign-in modal
  // would open again on the next page. We check the token to tell the two
  // cases apart: token is set as soon as login succeeds, so a real login
  // never hits this branch.
  useEffect(() => {
    const prev = prevPathnameRef.current;
    const curr = location.pathname;
    prevPathnameRef.current = curr;
    
    if (prev === ROUTES.LOGIN && curr !== ROUTES.LOGIN && !token) {
      dispatch(clearPendingAccessService());
    }
  }, [location.pathname, token, dispatch]);

  // Only clear pendingAccessService. Do NOT touch selectedServices: the
  // blocked service was already removed (or was never added), and other
  // services the user picked for a multi-booking must stay.
  const handleClose = useCallback(() => {
    dispatch(clearPendingAccessService());
  }, [dispatch]);

  // Re-add the unlocked service, tagging it with the membership that pays for
  // it (snapshotting name/rest/count for the summary).
  const resumeBooking = useCallback(
    (service, purchase) => {
      // No date works for both this membership and the ones already applied —
      // block now instead of failing at Confirm ("Membership is out of date").
      const coverageWindow = getMembershipCoverageWindow(selectedServices, membershipAccessGrants);
      if (!isPurchaseDateCompatible(purchase, coverageWindow)) {
        toast({
          title: "Error!",
          description: _t(
            "This service can't be booked together with your current selection - their memberships don't share an available date."
          ),
          variant: "error",
        });
        dispatch(clearPendingAccessService());
        return;
      }

      dispatch(setMembershipAccessGrant(createMembershipGrant(service, purchase)));
      // Set this BEFORE dispatching. The first effect will run again after the
      // service is added back, sees the id here, and skips the check.
      justResumedRef.current = service.id;
      dispatch(clearPendingAccessService());
      if (isClassService(service)) dispatch(setSelectedProvider(service.provider));
      applyServiceSelection(service, false);

      // Picked from the class list: continue as a plain class tap would.
      if (isClassService(service) && location.pathname === ROUTES.CLASSES) {
        navigate(getClassStartRoute(service, isPluginEnabled("paid_attributes")));
      }
    },
    [selectedServices, membershipAccessGrants, toast, _t, dispatch, applyServiceSelection, location.pathname, navigate, isPluginEnabled]
  );

  // Access is GRANTED via a single membership: resume automatically.
  // resumedRef stops this from running twice for the same service if React
  // re-renders.
  useEffect(() => {
    if (!pendingService) {
      resumedRef.current = null;
      return;
    }
    if (status !== ACCESS_STATUS.GRANTED) return;
    if (resumedRef.current === pendingService.id) return;

    resumedRef.current = pendingService.id;
    resumeBooking(pendingService, clientMembership);
  }, [pendingService, status, clientMembership, resumeBooking]);

  // Cart already spent every covering membership — toast and drop the service
  // instead of over-drawing at booking. Fires once per pending service.
  useEffect(() => {
    if (!pendingService) {
      exhaustedToastRef.current = null;
      return;
    }
    if (status !== ACCESS_STATUS.MEMBERSHIP_EXHAUSTED) return;
    if (exhaustedToastRef.current === pendingService.id) return;
    if (isSuppressedRoute(location.pathname) || isInPaymentCallback()) return;

    exhaustedToastRef.current = pendingService.id;
    toast({
      title: "Error!",
      description: _t("No available sessions left on purchased membership"),
      variant: "error",
    });
    dispatch(clearPendingAccessService());
  }, [pendingService, status, location.pathname, toast, _t, dispatch]);

  // The client picked one of several usable memberships in the choose modal.
  // Resume the same way GRANTED does, with the chosen instance id.
  const handleChoose = useCallback(
    (membershipId) => {
      if (!pendingService) return;

      const purchase = usableMemberships.find((m) => +m.id === +membershipId) || null;
      resumedRef.current = pendingService.id;
      resumeBooking(pendingService, purchase);
    },
    [pendingService, usableMemberships, resumeBooking]
  );

  if (!membershipPluginEnabled) return null;
  if (!pendingService) return null;
  // Same routes the first effect skips (status pages, /login while waiting
  // for sign-in to finish).
  if (isSuppressedRoute(location.pathname) || isInPaymentCallback()) return null;

  if (status === ACCESS_STATUS.DENIED_LOGIN) {
    return <MembershipSignInModal isOpen onClose={handleClose} service={pendingService} />;
  }
  if (status === ACCESS_STATUS.DENIED_MEMBERSHIP) {
    return (
      <MembershipRequiredModal
        isOpen
        onClose={handleClose}
        service={pendingService}
        requiredMemberships={requiredMemberships}
      />
    );
  }
  if (status === ACCESS_STATUS.CHOOSE_MEMBERSHIP) {
    // Grey out memberships with no date overlap with the already-applied ones.
    const coverageWindow = getMembershipCoverageWindow(selectedServices, membershipAccessGrants);
    const disabledIds = new Set(
      usableMemberships.filter((m) => !isPurchaseDateCompatible(m, coverageWindow)).map((m) => m.id)
    );
    return (
      <MembershipChooseModal
        isOpen
        onClose={handleClose}
        memberships={usableMemberships}
        disabledIds={disabledIds}
        onConfirm={handleChoose}
      />
    );
  }
  return null;
};

export default MembershipServiceAccessModals;
